import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiError, useToolsApi } from '../apis/tools-apis';
import { useTagsApi } from '../apis/tags-apis';
import { Tool } from '../types';
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Card, CardContent } from "../components/ui/Card";
import ToolForm, { ToolFormRef } from './ToolForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_TOOL_TIMEOUT } from '../config/api-config';

// Helper functions to reduce cognitive complexity
const shouldFetchData = (id: string | undefined, initialFetchMade: React.MutableRefObject<boolean>): boolean => {
  return !!(id && !initialFetchMade.current);
};

const extractToolNamesFromTools = (allTools: Tool[], currentToolId: string): string[] => {
  return allTools
    .filter(tool => tool.toolId !== currentToolId)
    .map(tool => tool.toolName);
};

const extractTagsFromTools = (allTools: Tool[]): string[] => {
  const allTags = new Set<string>();
  allTools.forEach(tool => {
    tool.toolTags?.forEach(tag => {
      if (tag) allTags.add(tag);
    });
  });
  return Array.from(allTags);
};

const createNormalizedTool = (tool: Tool): Tool => {
  return {
    ...tool,
    toolTags: tool.toolTags || [],
    toolTimeout: tool.toolTimeout || DEFAULT_TOOL_TIMEOUT,
    accessControl: tool.accessControl || {
      viewAccessType: 'Owner Only',
      viewAllowedUsers: [],
      editAccessType: 'Owner Only',
      editAllowedUsers: []
    }
  };
};

const handleApiError = (
  error: any, 
  navigate: (path: string) => void, 
  addToast: (toast: any) => void
): void => {
  if (error instanceof ApiError && error.status === 403) {
    navigate('/studio/unauthorized');
    return;
  }
  
  const errorMessage = error instanceof ApiError 
    ? error.message 
    : "Failed to fetch tool details";
    
  addToast({
    title: "Error",
    description: errorMessage,
    variant: "destructive",
    duration: 5000
  });
  navigate('/studio/tools');
};

const handleToolNotFound = (
  navigate: (path: string) => void,
  addToast: (toast: any) => void
): void => {
  addToast({
    title: "Error",
    description: "Tool not found",
    variant: "destructive",
    duration: 4000
  });
  navigate('/studio/tools');
};

// Helper function to load tag suggestions
const loadTagSuggestionsFromApi = async (
  tagsApi: any,
  allTools: Tool[],
  setTagSuggestions: (tags: string[]) => void
) => {
  try {
    const globalTags = await tagsApi.getTags();
    setTagSuggestions(globalTags.map((tag: any) => tag.tagName));
  } catch (error) {
    console.error('Error fetching global tags:', error);
    // Fallback to extracting tags from existing tools if global tags fail
    const fallbackTags = extractTagsFromTools(allTools);
    setTagSuggestions(fallbackTags);
  }
};

const useToolData = (
  toolsApi: any,
  id: string | undefined
) => {
  const fetchToolData = async () => {
    if (!id) throw new Error('No tool ID provided');
    
    const getAllTools = true;
    const tool = await toolsApi.getTool(id);
    const allTools = await toolsApi.getTools(getAllTools);
    
    return { tool, allTools };
  };

  return { fetchToolData };
};



export const EditToolPage: React.FC = () => {
  const formRef = useRef<ToolFormRef>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const tagsApi = useTagsApi();
  const [editedTool, setEditedTool] = useState<Tool | null>(null);
  const [originalTool, setOriginalTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const initialFetchMade = useRef(false);
  const [existingToolNames, setExistingToolNames] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const { user } = useAuth();
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();

  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  
  // Use custom hooks for complex operations
  const { fetchToolData } = useToolData(toolsApi, id);
  
  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(editedTool) !== JSON.stringify(originalTool);

  // Handle browser back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      // Only handle if there are unsaved changes
      if (hasUnsavedChanges) {
        backButtonClicked.current = true;
        setShowNavigationWarning(true);
        setPendingNavigation('/studio/tools');
      } else {
        // If no unsaved changes, allow normal back navigation
        navigate('/studio/tools');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Add a single history state entry if not already added
    if (!historyStateAdded.current && hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
      historyStateAdded.current = true;
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate, setShowNavigationWarning, setPendingNavigation]);

  // Simplified data processing functions
  const processToolData = async (tool: Tool, allTools: Tool[]) => {
    if (!tool) {
      handleToolNotFound(navigate, addToast);
      return;
    }

    const normalizedTool = createNormalizedTool(tool);
    setEditedTool(normalizedTool);
    setOriginalTool(normalizedTool);
    
    const toolNames = extractToolNamesFromTools(allTools, id!);
    setExistingToolNames(toolNames);
    
    await loadTagSuggestionsFromApi(tagsApi, allTools, setTagSuggestions);
  };

  const handleFetchError = (error: any) => {
    console.error('Error fetching data:', error);
    handleApiError(error, navigate, addToast);
  };

  const fetchToolAndNames = async () => {
    if (!shouldFetchData(id, initialFetchMade)) {
      return;
    }

    initialFetchMade.current = true;
    setIsLoading(true);

    try {
      const { tool, allTools } = await fetchToolData();
      await processToolData(tool, allTools);
    } catch (error: any) {
      handleFetchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchToolAndNames();
  }, [id, addToast, navigate, toolsApi]);

  const handleSubmit = async () => {
    if (!editedTool) {
      addToast({
        title: "Error",
        description: "No tool data available to save",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const toolToUpdate = {
        ...editedTool,
        toolName: editedTool.toolName.trim(),
        toolDescription: editedTool.toolDescription.trim(),
        toolUrlEndpoint: editedTool.toolUrlEndpoint.trim(),
        toolTimeout: editedTool.toolTimeout || DEFAULT_TOOL_TIMEOUT,
        toolParameters: {
          parameters: editedTool.toolParameters?.parameters || [],
          testMode: editedTool.toolParameters?.testMode ?? true
        },
        toolOutput: editedTool.toolOutput || { parameters: {} },
        toolAnnotation: editedTool.toolAnnotation || '',
        toolTags: editedTool.toolTags || [],  // Ensure tags are initialized
        isCore: editedTool.isCore ?? false // This is currently not set in the UI
      };
  
      if (editedTool.toolAuthentication && editedTool.toolAuthentication !== '••••••••') {
        toolToUpdate.toolAuthentication = editedTool.toolAuthentication.trim();
      }
  
      const updatedTool = await toolsApi.updateTool(toolToUpdate);
      
      // Ensure tags are initialized in the response
      const normalizedTool = {
        ...updatedTool,
        toolTags: updatedTool.toolTags || []
      };
      
      setOriginalTool(normalizedTool);
      setEditedTool(normalizedTool);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: `Tool ${toolToUpdate.toolName} has been successfully updated`,
        variant: "default",
        duration: 4000
      });
  
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      } else {
        navigate(`/studio/tools/test/${toolToUpdate.toolId}`);
      }
    } catch (error) {
      console.error('Error updating tool:', error);
      addToast({
        title: "Error",
        description: "Failed to update tool. Please try again.",
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setEditedTool(originalTool);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setEditedTool(originalTool);
    setIsReadOnly(true);
    
    if (backButtonClicked.current) {
      backButtonClicked.current = false;
      historyStateAdded.current = false;
      navigate('/studio/tools');
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    backButtonClicked.current = false;
    
    // If this was a back button navigation, restore the history state
    if (backButtonClicked.current) {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/tools');
    } else {
      navigate('/studio/tools');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Tool...</p>
      </div>
    );
  }

  if (!editedTool) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Tool not found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <ToolForm 
        ref={formRef}
        tool={editedTool}
        originalTool={originalTool}
        onToolChange={setEditedTool}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        tagSuggestions={tagSuggestions}
        existingToolNames={existingToolNames}
        currentUserId={user?.sub || ''}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditToolPage;